import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import marketplaceTemplates from '../data/marketplaceTemplates';
import { getCommunityBuildStacks, getClientBuildStacks, publishBuildStack } from '../api/buildStacks';

export interface BuildStep {
  beeName: string;
  command: string;
  inputs?: Record<string, unknown>;
  onError?: 'halt' | 'continue';
  outputsVar?: string;
}

export interface BuildStack {
  id: string;
  name: string;
  description?: string;
  steps: BuildStep[];
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  icon?: string;
  category: 'pre-made' | 'client' | 'community';
}

interface BuildStacksState {
  stacks: BuildStack[];
  activeStackId?: string;
  searchQuery: string;
  categoryFilter: 'all' | 'pre-made' | 'client' | 'community';
  isLoading: boolean;
  error: string | null;
  load: () => void;
  setActive: (id?: string) => void;
  setSearchQuery: (query: string) => void;
  setCategoryFilter: (category: 'all' | 'pre-made' | 'client' | 'community') => void;
  refresh: () => void;
  loadCommunityStacks: () => Promise<void>;
  loadClientStacks: (clientId: string) => Promise<void>;
  publishStack: (id: string) => Promise<void>;
  add: (bs: Omit<BuildStack, 'id' | 'createdAt' | 'updatedAt'>) => BuildStack;
  update: (bs: BuildStack) => void;
  remove: (id: string) => void;
  getFilteredStacks: () => BuildStack[];
}

export const useBuildStacksStore = create<BuildStacksState>()(
  persist(
    (set, get) => ({
      stacks: [],
      activeStackId: undefined,
      searchQuery: '',
      categoryFilter: 'all',
      isLoading: false,
      error: null,
      
      getFilteredStacks: () => {
        const state = get();
        return state.stacks.filter(stack => {
          const matchesSearch = stack.name.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
            (stack.description && stack.description.toLowerCase().includes(state.searchQuery.toLowerCase()));
          const matchesCategory = state.categoryFilter === 'all' || stack.category === state.categoryFilter;
          return matchesSearch && matchesCategory;
        });
      },
      
      load: () => {
        const preMadeStacks = marketplaceTemplates.map(template => ({
          ...template,
          category: 'pre-made' as const
        }));
        set(state => ({ 
          stacks: [...preMadeStacks, ...state.stacks.filter(s => s.category !== 'pre-made')] 
        }));
      },
      
      setActive: (id?: string) => {
        set({ activeStackId: id });
      },
      
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },
      
      setCategoryFilter: (category: 'all' | 'pre-made' | 'client' | 'community') => {
        set({ categoryFilter: category });
      },
      
      refresh: () => {
        get().load();
      },
      
      loadCommunityStacks: async () => {
        set({ isLoading: true, error: null });
        try {
          const communityStacks = await getCommunityBuildStacks();
          set(state => ({
            stacks: [
              ...state.stacks.filter(s => s.category !== 'community'),
              ...communityStacks
            ],
            isLoading: false
          }));
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to load community BuildStacks' 
          });
        }
      },
      
      loadClientStacks: async (clientId: string) => {
        set({ isLoading: true, error: null });
        try {
          const clientStacks = await getClientBuildStacks(clientId);
          set(state => ({
            stacks: [
              ...state.stacks.filter(s => s.category !== 'client'),
              ...clientStacks
            ],
            isLoading: false
          }));
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to load client BuildStacks' 
          });
        }
      },
      
      publishStack: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await publishBuildStack(id);
          set(state => ({
            stacks: state.stacks.map(stack => 
              stack.id === id 
                ? { ...stack, category: 'community' as const }
                : stack
            ),
            isLoading: false
          }));
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Failed to publish BuildStack' 
          });
        }
      },
      
      add: (input) => {
        const newStack: BuildStack = {
          ...input,
          id: `stack_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set(state => ({ stacks: [...state.stacks, newStack] }));
        return newStack;
      },
      
      update: (bs) => {
        const updated = { ...bs, updatedAt: new Date().toISOString() };
        set(state => ({
          stacks: state.stacks.map(s => s.id === bs.id ? updated : s)
        }));
      },
      
      remove: (id) => {
        set(state => ({
          stacks: state.stacks.filter(s => s.id !== id),
          activeStackId: state.activeStackId === id ? undefined : state.activeStackId
        }));
      }
    }),
    { name: 'beehive-buildstacks-storage' }
  )
);
